import React, { useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import { formatDateLocale } from '../../../../../lib/utils.es6'
import {
  diffQuotations,
  fetchAmendmentContext,
  payerDetails,
  payerIsSomeoneElse,
  policyHolderDetails
} from './quotationDiff.es6'

// The checker deciding an amendment needs to see what the member changed, not
// just the resulting quotation. This panel pairs the amended quotation with the
// one it supersedes and lists the differences: cover, principal, payment,
// beneficiary, and the members added, removed, or edited.
//
// The previous quotation is fetched on first expand rather than up front — a
// dashboard page can hold many amendments and most are opened one at a time.

const headerCellStyle = {
  fontFamily: 'Open Sans',
  fontSize: '12px',
  fontWeight: 600,
  color: '#718096',
  borderBottom: '1px solid #e2e8f0',
  py: 1
}

const cellStyle = {
  fontFamily: 'Open Sans',
  fontSize: '13px',
  borderBottom: '1px solid #f1f4f8',
  py: 1
}

const ChangeTable = ({ title, rows }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, color: '#032a37', mb: 0.5 }}
    >
      {title}
    </Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={Object.assign({}, headerCellStyle, { width: '34%' })}>
            Field
          </TableCell>
          <TableCell sx={Object.assign({}, headerCellStyle, { width: '33%' })}>
            Before
          </TableCell>
          <TableCell sx={Object.assign({}, headerCellStyle, { width: '33%' })}>
            After
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.label}-${index}`}>
            <TableCell sx={cellStyle}>{row.label}</TableCell>
            <TableCell sx={Object.assign({}, cellStyle, { color: '#8F191C' })}>
              {row.before}
            </TableCell>
            <TableCell
              sx={Object.assign({}, cellStyle, {
                color: '#0b6b3a',
                fontWeight: 600
              })}
            >
              {row.after}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
)

const MemberList = ({ title, colour, members }) => {
  if (!members.length) return null
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: colour, mb: 0.5 }}
      >
        {`${title} (${members.length})`}
      </Typography>
      {members.map((member, index) => (
        <Typography
          key={`${member.name}-${index}`}
          variant="body2"
          sx={{ fontSize: '13px', color: '#2d3748' }}
        >
          {`• ${member.name} — ${member.relationship} — ${member.premium}`}
        </Typography>
      ))}
    </Box>
  )
}

const DetailBlock = ({ title, note, rows }) => {
  if (!rows.length) return null
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: '#032a37' }}
      >
        {title}
      </Typography>
      {note ? (
        <Typography variant="caption" sx={{ color: '#718096' }}>
          {note}
        </Typography>
      ) : null}
      <Grid container spacing={1} sx={{ mt: 0.5 }}>
        {rows.map(row => (
          <Grid item xs={12} sm={6} md={4} key={row.label}>
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', color: '#718096' }}
            >
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '13px', fontWeight: 500, color: '#2d3748' }}
            >
              {row.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

const EMPTY_CONTEXT = {
  previous: null,
  policy: null,
  customer: null,
  failed: false
}

const QuotationChanges = ({ quote, currencyCode }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [context, setContext] = useState(EMPTY_CONTEXT)

  const ad = quote.additional_details || {}
  const previous = context.previous
  // A request that errored is not the same as a record with no predecessor, and
  // it is worth retrying. `loaded` stays false on failure so a retry can run.
  const failed = context.failed

  const load = () => {
    setLoading(true)
    fetchAmendmentContext(quote)
      .then(resolved => {
        const next = resolved || EMPTY_CONTEXT
        setContext(next)
        setLoaded(!next.failed)
      })
      .catch(() =>
        setContext(Object.assign({}, EMPTY_CONTEXT, { failed: true }))
      )
      .finally(() => setLoading(false))
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && !loaded && !loading) load()
  }

  const diff = previous ? diffQuotations(previous, quote, currencyCode) : null

  // Names the state being compared against by its date, never by a guid — a
  // guid tells the person deciding the amendment nothing they can use. Prefer
  // the date the cover took effect; created_at is when the record was written,
  // which is not the same thing, so the wording changes with the source.
  const effectiveFrom = previous
    ? previous.start_date ||
      (previous.additional_details || {}).start_date ||
      null
    : null
  const baselineLabel = effectiveFrom
    ? `Compared against the cover in force since ${formatDateLocale(
        effectiveFrom
      )}`
    : previous && previous.created_at
    ? `Compared against the quotation created on ${formatDateLocale(
        previous.created_at
      )}`
    : 'Compared against the cover previously in force'

  // Who the amendment is for and who pays for it. This is context rather than a
  // change, so it shows even when no baseline could be resolved — a checker can
  // still see whose policy this is.
  const holderRows = loaded ? policyHolderDetails(quote, context) : []
  const payerRows =
    loaded && payerIsSomeoneElse(quote, context) ? payerDetails(quote) : []

  return (
    <Box sx={{ mt: 2, borderTop: '1px solid #e2e8f0', pt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box>
          {ad.amendment_reason ? (
            <Typography variant="body2" sx={{ color: '#2d3748' }}>
              <strong>Reason:</strong> {ad.amendment_reason}
            </Typography>
          ) : null}
          {ad.amended_at ? (
            <Typography variant="body2" sx={{ color: '#718096' }}>
              <strong>Amended:</strong> {formatDateLocale(ad.amended_at)}
            </Typography>
          ) : null}
        </Box>
        <ButtonInc variant="outlined" onClick={toggle}>
          {open ? 'Hide changes' : 'View changes'}
        </ButtonInc>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Comparing this amendment with the previous quotation...
              </Typography>
            </Box>
          ) : null}

          {!loading ? (
            <>
              <DetailBlock title="Policy holder" rows={holderRows} />
              <DetailBlock
                title="Premium payer"
                note="Paid by someone other than the policy holder"
                rows={payerRows}
              />
            </>
          ) : null}

          {!loading && failed ? (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ color: '#8F191C' }}>
                The cover this amendment replaces could not be loaded. This is a
                problem reaching the record, not a sign that there is nothing to
                compare.
              </Typography>
              <ButtonInc
                variant="outlined"
                onClick={load}
                style={{ marginTop: '8px' }}
              >
                Try again
              </ButtonInc>
            </Box>
          ) : null}

          {!loading && loaded && !previous ? (
            <Typography variant="body2" sx={{ color: '#8F191C', py: 1 }}>
              This amendment does not record the cover it replaces, so there is
              nothing to compare it against.
            </Typography>
          ) : null}

          {/* Wording tracks the withheld count: saying "no differences" directly
              above "N fields could not be compared" reads as a contradiction. */}
          {!loading && diff && !diff.hasChanges ? (
            <Typography variant="body2" sx={{ color: '#718096', py: 1 }}>
              {diff.withheld > 0
                ? 'No comparable differences found between this amendment and the cover it replaces.'
                : 'No differences found between this amendment and the cover it replaces.'}
            </Typography>
          ) : null}

          {/* Never drop fields silently: the older record is often far sparser
              than the amendment, and a field it never stored cannot honestly be
              called a change. Say how many were held back for that reason. */}
          {!loading && diff && diff.withheld > 0 ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: '#718096', mb: 2 }}
            >
              {`${diff.withheld} further field${
                diff.withheld === 1 ? '' : 's'
              } could not be compared — the cover being replaced has no value
              recorded for ${diff.withheld === 1 ? 'it' : 'them'}, so whether
              ${diff.withheld === 1 ? 'it' : 'they'} changed cannot be told from
              the record.`}
            </Typography>
          ) : null}

          {!loading && diff && diff.hasChanges ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Chip size="small" variant="outlined" label={baselineLabel} />
              </Box>

              {diff.groups.map(group => (
                <ChangeTable
                  key={group.title}
                  title={group.title}
                  rows={group.rows}
                />
              ))}

              <MemberList
                title="Members added"
                colour="#0b6b3a"
                members={diff.members.added}
              />
              <MemberList
                title="Members removed"
                colour="#8F191C"
                members={diff.members.removed}
              />

              {diff.members.changed.map((member, index) => (
                <ChangeTable
                  key={`${member.name}-${index}`}
                  title={`Member updated: ${member.name} (${member.relationship})`}
                  rows={member.rows}
                />
              ))}
            </>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  )
}

export default QuotationChanges
